import { Component } from '@opten/compiler/metadata/component';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import { Collapse } from '@opten/collapse/initializer';
import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';
import { createNode } from '@opten/dom/create-node';
import { scrollToElement } from '@opten/scroll/scroll-to-element';
import { linearTween } from '@opten/animation/linear-tween';
import { isElementScrolledOutsideView } from '@opten/viewport/is-element-scrolled-outside-view';

import { isPad } from '../utilities/detection';
import { getStickyOffset } from '../utilities/scroll-helper';

const transitionDuration = 600;

@Component({ className: 'js-customer-pages-full-preview' })
export default class CustomerPagesFullPreview implements OnInit {
    private _selectElement: HTMLSelectElement;
    private _elements: Array<HTMLElement>;
    private _backdropElement: HTMLElement;

    constructor(private element: HTMLElement) {
        this._selectElement = this.element.getElementsByClassName(
            'js-customer-pages-full-preview-select'
        )[0] as HTMLSelectElement;

        this._backdropElement = this.element.getElementsByClassName(
            'js-customer-pages-full-preview-backdrop'
        )[0] as HTMLElement;

        this._elements = Array.from(
            this.element.getElementsByClassName(
                'js-customer-pages-full-preview-element'
            )
        ) as Array<HTMLElement>;
    }

    onInit() {
        this._selectElement.addEventListener('change', () => {
            const element = document.getElementById(this._selectElement.value);

            if (element) {
                const target = new Collapse(element);
                const body = document.getElementsByClassName('js-body')[0];

                if (
                    isElementScrolledOutsideView(
                        element.getBoundingClientRect(),
                        [body.getBoundingClientRect()]
                    )
                ) {
                    scrollToElement(
                        this.element,
                        linearTween,
                        300,
                        getStickyOffset() * -1
                    );
                }

                if (!target.isOpen()) {
                    target.show();
                }
            }
        });

        this._handleResize();
        window.addEventListener('resize', () => this._handleResize());
    }

    private _handleResize() {
        if (isPad()) {
            this._elements.forEach(element => {
                element.removeEventListener(
                    'mouseenter',
                    this._handleMouseEnter
                );
                element.removeEventListener(
                    'mouseleave',
                    this._handleMouseLeave
                );
            });
        } else {
            this._elements.forEach(element => {
                element.addEventListener('mouseenter', this._handleMouseEnter);
                element.addEventListener('mouseleave', this._handleMouseLeave);
            });
        }
    }

    private _handleMouseEnter = (event: MouseEvent) => {
        const element = event.target as HTMLElement;

        const width = this.element.offsetHeight;
        const left =
            element.parentElement.offsetLeft +
            element.offsetWidth / 2 -
            width / 2;

        this._backdropElement.style.left = `${left}px`;
        this._backdropElement.style.width = `${width}px`;

        const svg = this._backdropElement.getElementsByClassName(
            'c-customer-pages-full-preview__backdrop-triangle'
        )[0] as HTMLElement;

        if (this._redrawSvg(svg, width)) {
            if (svg) {
                // svg doesn't support parentElement in IE
                const parent = svg.parentNode || svg.parentElement;
                parent.removeChild(svg);
            }
            this._backdropElement.appendChild(this._createSvg(width));
        }

        addClass(this._backdropElement, '--show');
    }

    private _handleMouseLeave = (event: MouseEvent) => {
        const element = event.target as HTMLElement;

        removeClass(this._backdropElement, '--show');
        this._backdropElement.style.left = null;
        this._backdropElement.style.width = null;
    }

    private _redrawSvg(svg: HTMLElement, width: number): boolean {
        if (!svg) {
            return true;
        }

        return svg.getBoundingClientRect().width !== width;
    }

    private _createSvg(height: number) {
        // https://css-tricks.com/easing-linear-gradients/
        const svg = `<svg class="c-customer-pages-full-preview__backdrop-triangle"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="fill" x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" style="stop-color:rgb(255,255,255);stop-opacity:1" />
                    <stop offset="19%" style="stop-color:rgb(255,255,255);stop-opacity:0.738" />
                    <stop offset="34%" style="stop-color:rgb(255,255,255);stop-opacity:0.541" />
                    <stop offset="47%" style="stop-color:rgb(255,255,255);stop-opacity:0.382" />
                    <stop offset="56.5%" style="stop-color:rgb(255,255,255);stop-opacity:0.278" />
                    <stop offset="65%" style="stop-color:rgb(255,255,255);stop-opacity:0.194" />
                    <stop offset="73%" style="stop-color:rgb(255,255,255);stop-opacity:0.126" />
                    <stop offset="80.2%" style="stop-color:rgb(255,255,255);stop-opacity:0.075" />
                    <stop offset="86.1%" style="stop-color:rgb(255,255,255);stop-opacity:0.042" />
                    <stop offset="91%" style="stop-color:rgb(255,255,255);stop-opacity:0.021" />
                    <stop offset="95.2%" style="stop-color:rgb(255,255,255);stop-opacity:0.008" />
                    <stop offset="98.2%" style="stop-color:rgb(255,255,255);stop-opacity:0.002" />
                    <stop offset="100%" style="stop-color:rgb(255,255,255);stop-opacity:0" />
                </linearGradient>
            </defs>
            <polygon points="${height /
            2} 0, 0 ${height}, ${height} ${height}" fill="url(#fill)" />
        </svg>`;

        const element = createNode(svg);

        element.style.width = `${height}px`;
        element.style.height = `${height}px`;

        return element;
    }
}
